import type { InteractionEvent, LedgerState, Message } from '../../shared/types';
import {
  createSignedP2PEnvelopeProof,
  derivePeerIdFromPub,
  p2pDataChannelSigningPayload,
  p2pSignalingSigningPayload,
  verifySignedP2PEnvelopeProof,
  type SeaSigningPair,
} from '../../shared/p2p-runtime';
import {
  buildHandshakePayload,
  buildHandshakeDiagnostics,
  negotiateProtocol,
  validateHandshakePayload,
  type HandshakeDiagnostics,
  type P2PHandshakePayload,
} from '../../shared/p2p-handshake';
import type { P2PMeshFrame } from '../../shared/p2p-mesh-protocol';
import { messageIntroducesGap } from '../../shared/conversation-reconcile';
import { readNativeHostInfo } from '../ui/native-host-info';
import { evaluateBuildTrust, type BuildTrustLabel, type OfficialBuildCredential } from '../../shared/official-build-credential';
import { verifyOfficialBuildCredentialSignature } from '../../shared/official-build-credential-signature';
import { BoundedNonceCache } from '../../shared/p2p-abuse-defense';
import {
  CompositeSignalingTransport,
  encodeSignalingPayload,
  type PostSignalingBody,
  type SignalingTransport,
} from './signaling-transport';
import { GunPubSubSignaler } from './gun-pubsub-signaler';
import { HttpRelaySignaler } from './http-relay-signaler';
import { selectedCandidatePairFromStats, type SelectedCandidatePair } from '../../shared/wifi-direct-link';
import {
  decryptSecureSessionJson,
  deriveSecureSession,
  encryptSecureSessionJson,
  generateSecureSessionOffer,
  pinnedIdentityAccepts,
  type GeneratedSecureSessionOffer,
} from '../../shared/p2p-secure-session';

export type P2PConnectionState = 'idle' | 'connecting' | 'connected' | 'failed';

type SignalPayload =
  | { type: 'offer'; sdp: RTCSessionDescriptionInit; offerId?: string }
  | { type: 'answer'; sdp: RTCSessionDescriptionInit; offerId?: string }
  | { type: 'ice'; candidate: RTCIceCandidateInit | null; offerId?: string }
  /** The answering side rebuilt its connection and asks the offering side for a fresh offer. */
  | { type: 'restart'; requestId: string; at: number };

/** A restart request older than this is a replay from the signaling queue, not a live request. */
const RESTART_REQUEST_MAX_AGE_MS = 20_000;

/** Binary ↔ base64 helpers for streaming attachment chunks over the text DataChannel. */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

type HandshakeWirePayload = {
  type: 'handshake';
  payload: P2PHandshakePayload;
};

type DmWirePayload = {
  type: 'dm';
  message: {
    id: string;
    senderId: string;
    text: string;
    timestamp: string;
    channel: string;
    transport: string;
    encryption?: 'sea-ecdh-v1';
    prevSeen?: string;
    isFromChatbot?: boolean;
    /** Per-matched-talk thread scope (redesign §5); absent = pair DM thread. */
    talkId?: string;
  };
};

type LedgerStateWirePayload = {
  type: 'ledger-state';
  feeds: LedgerState;
};

type LedgerSyncRequestWirePayload = {
  type: 'ledger-sync-request';
};

type LedgerEventsWirePayload = {
  type: 'ledger-events';
  events: InteractionEvent[];
};

type MeshWirePayload = {
  type: 'mesh';
  frame: P2PMeshFrame;
};

/** Phase 5: "here are the message ids I hold for this conversation" — peer backfills the rest. */
type SyncDigestWirePayload = {
  type: 'sync-digest';
  conversationId: string;
  messageIds: string[];
};

/** Recipient asks the sender to stream a shared attachment's bytes over the DataChannel. */
type AttachRequestWirePayload = {
  type: 'attach-request';
  cid: string;
};

/** One chunk of an attachment's bytes (base64), reassembled by seq up to `total`. */
type AttachChunkWirePayload = {
  type: 'attach-chunk';
  cid: string;
  seq: number;
  total: number;
  dataB64: string;
};

/** Link-upgrade control (Wi-Fi Direct negotiation, `src/shared/wifi-direct-link.ts`). The body is
 *  validated by the consumer; the signed envelope already binds it to the peer's SEA pub. */
type LinkUpgradeWirePayload = {
  type: 'link-upgrade';
  message: unknown;
};

/** Pair-private conversation/contact state. The signed DTLS channel is the only transport. */
type ConversationControlWirePayload = {
  type: 'conversation-control';
  metadata: Record<string, unknown>;
};

type ChannelFramePayload =
  | HandshakeWirePayload
  | LinkUpgradeWirePayload
  | DmWirePayload
  | LedgerStateWirePayload
  | LedgerSyncRequestWirePayload
  | LedgerEventsWirePayload
  | MeshWirePayload
  | SyncDigestWirePayload
  | AttachRequestWirePayload
  | AttachChunkWirePayload
  | ConversationControlWirePayload;

type SecureConfirmPayload = {
  type: 'secure-confirm';
  sessionId: string;
};

type SignedChannelWirePayload = {
  type: 'signed-frame';
  peerId: string;
  pub: string;
  timestamp: string;
  nonce: string;
  payloadHash: string;
  signature: string;
  frame: ChannelFramePayload;
};

type EncryptedChannelWirePayload = {
  type: 'encrypted-frame';
  peerId: string;
  pub: string;
  timestamp: string;
  nonce: string;
  payloadHash: string;
  signature: string;
  sessionId: string;
  iv: string;
  ciphertext: string;
};

type ChannelWirePayload = SignedChannelWirePayload | EncryptedChannelWirePayload;

/** Local/same-machine peers should connect well under this; longer waits usually mean a bug. */
export const P2P_WEBRTC_CONNECT_TIMEOUT_MS = 10_000;

/**
 * After a connect attempt fails, don't retry a fresh full-length ICE/DTLS cycle for every
 * subsequent send within this window — go straight back to "failed" so the caller's
 * offline-mailbox fallback (Phase 4, spec §19.4) kicks in immediately instead of the sender
 * blocking another P2P_WEBRTC_CONNECT_TIMEOUT_MS on each message. Without this, a peer that's
 * genuinely unreachable (offline, blocked UDP, NAT that never resolves) turns "send message"
 * into a repeated 10s hang per call — a real latency bug, not just an E2E environment quirk.
 * A fresh signal (new remote ICE/answer arriving) still clears this via setState('connecting').
 *
 * OPT-IN ONLY (config.failFastAfterFailure): the DM send path enables it; the peer-mesh
 * service must NOT — mesh neighbor re-formation (e.g. after hub API loss) deliberately
 * retries ensureConnected() in tight loops, and a fail-fast window starves those retries
 * so neighbors never re-form.
 */
export const P2P_WEBRTC_RETRY_COOLDOWN_MS = 15_000;

/**
 * A DataChannel whose encrypted handshake is not confirmed within this long after it opens is
 * failed, so the session is rebuilt. Without it, a pair whose two sides derived different
 * sessions stayed "connected" forever with every frame undeliverable (seen on phones 2026-10-18).
 */
export const P2P_SECURE_HANDSHAKE_TIMEOUT_MS = 15_000;

/** HTTP signaling poll interval while the link is connected (600 ms otherwise). */
export const P2P_CONNECTED_SIGNAL_POLL_MS = 15_000;

/**
 * Returns the ICE server list for `RTCPeerConnection`.
 *
 * Connection priority order (§4.4):
 *   1. local / host candidates   — same-machine or LAN (no STUN/TURN needed)
 *   2. direct IP / srflx         — STUN-derived server-reflexive candidates
 *   3. NAT hole punch            — STUN/ICE coordinated traversal
 *   4. relay / TURN fallback     — only when all direct paths fail
 *
 * The browser WebRTC engine applies this ordering automatically via RFC 5245
 * candidate priority values: host > srflx > relay.  This function configures
 * which STUN/TURN servers are available; actual candidate selection is left
 * to the browser.  A TURN relay should be added to the returned list when
 * relay fallback (priority 4) is required in production.
 */
export function defaultIceServers(): RTCIceServer[] {
  const custom = typeof process !== 'undefined' ? process.env.E2E_WEBRTC_ICE_SERVERS : undefined;
  if (custom && custom.trim()) {
    try {
      const parsed = JSON.parse(custom) as RTCIceServer[];
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // fall through
    }
  }
  // E2E webpack sets DISABLE_HMR=true — peers run on the same machine,
  // so host candidates (priority 1) are always used; STUN is not needed.
  if (typeof process !== 'undefined' && process.env.DISABLE_HMR === 'true') {
    return [];
  }
  // Production: provide STUN for NAT traversal (priorities 2–3).
  // Priority 4 relay requires a TURN server; see resolveIceServers() below.
  return [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ];
}

type CachedTurnServer = { server: RTCIceServer; expiresAtMs: number };
let cachedTurnServer: CachedTurnServer | null = null;
const TURN_RETRY_AFTER_FAILURE_MS = 60_000;
let turnUnavailableUntilMs = 0;

async function fetchTurnServer(apiBase: string): Promise<{ server: RTCIceServer; ttlSeconds: number } | null> {
  try {
    const res = await fetch(`${apiBase}/api/turn-credentials`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { urls?: string[]; username?: string; credential?: string; ttl?: number };
    if (!Array.isArray(body.urls) || body.urls.length === 0 || !body.username || !body.credential) return null;
    return {
      server: { urls: body.urls, username: body.username, credential: body.credential },
      ttlSeconds: typeof body.ttl === 'number' && body.ttl > 0 ? body.ttl : 3600,
    };
  } catch {
    return null;
  }
}

/**
 * OPEN-36 offline mode: while this phone is in a Wi-Fi Direct group, its loopback TURN relay (relay
 * socket on the group address) is offered to every new connection. LAN and internet peers still
 * connect on their usual pairs; a peer reachable only across the group connects relay↔relay
 * through the two phones' relays. Android WebView never gathers candidates on the group interface.
 */
let localLinkIceServer: RTCIceServer | null = null;

export function setLocalLinkIceServer(server: RTCIceServer | null): void {
  localLinkIceServer = server;
}

export async function resolveIceServers(apiBase: string): Promise<RTCIceServer[]> {
  const servers = await resolveDefaultIceServers(apiBase);
  return localLinkIceServer ? [...servers, localLinkIceServer] : servers;
}

/**
 * Same priority-ordered ICE server list as defaultIceServers(), plus a TURN relay (priority 4)
 * fetched from the relay's own `/api/turn-credentials` endpoint when one is configured
 * server-side (`TURN_SHARED_SECRET`/`TURN_SERVER_HOST` — see
 * docs/IinPublic_VPS_Installation_Guide.md's TURN section). Falls back to STUN-only, exactly
 * matching `defaultIceServers()`, if the relay has no TURN server configured or the credential
 * fetch fails for any reason — a missing/unreachable TURN server must never block a connection
 * STUN alone can still complete.
 *
 * Short-lived credentials (coturn's `use-auth-secret` convention) are cached in memory and only
 * re-fetched once they're close to expiry, so a burst of P2P session starts doesn't each pay a
 * network round trip.
 */
async function resolveDefaultIceServers(apiBase: string): Promise<RTCIceServer[]> {
  const base = defaultIceServers();
  // E2E override / same-machine E2E (empty list) already fully determine the answer — a TURN
  // fetch would only add latency and a network dependency E2E specs don't need.
  const e2eOverride = typeof process !== 'undefined' ? process.env.E2E_WEBRTC_ICE_SERVERS : undefined;
  const sameMachineE2e = typeof process !== 'undefined' && process.env.DISABLE_HMR === 'true';
  if ((e2eOverride && e2eOverride.trim()) || sameMachineE2e) return base;

  const now = Date.now();
  if (cachedTurnServer && cachedTurnServer.expiresAtMs > now) {
    return [...base, cachedTurnServer.server];
  }
  if (turnUnavailableUntilMs > now) return base;
  const fetched = await fetchTurnServer(apiBase);
  if (!fetched) {
    // Offline (no hub) the embedded node's forward takes its full timeout; don't pay that on
    // every session start.
    turnUnavailableUntilMs = now + TURN_RETRY_AFTER_FAILURE_MS;
    return base;
  }
  // Refresh 5 minutes before actual expiry so a session starting near the boundary never races
  // an about-to-expire credential.
  cachedTurnServer = { server: fetched.server, expiresAtMs: now + Math.max(0, fetched.ttlSeconds - 300) * 1000 };
  return [...base, fetched.server];
}

/**
 * Device-testing aid only: `?p2p_drop_remote_candidates=192.168.10.` makes this device ignore
 * remote *host* ICE candidates whose address starts with that prefix, simulating two phones on
 * different networks while they actually share one LAN (OPEN-36 Wi-Fi Direct verification); relay
 * and server-reflexive candidates still work. Absent in normal launches — MainActivity forwards it
 * only from an explicit adb `--es` extra.
 */
function debugDroppedRemoteCandidatePrefix(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const prefix = new URLSearchParams(window.location.search).get('p2p_drop_remote_candidates');
  return prefix && /^[0-9a-f.:]{2,40}$/i.test(prefix) ? prefix : null;
}

function isDroppedHostCandidate(candidate: RTCIceCandidateInit, prefix: string): boolean {
  // candidate:<foundation> <component> <transport> <priority> <address> <port> typ <type> ...
  const parts = (candidate.candidate ?? '').split(' ');
  // mDNS-obfuscated host candidates (`<uuid>.local`) hide the address; treat them as on-LAN too.
  return parts[7] === 'host' && (!!parts[4]?.startsWith(prefix) || !!parts[4]?.endsWith('.local'));
}

export type P2PSessionConfig = {
  apiBase: string;
  /** Enable the post-failure fail-fast window (see P2P_WEBRTC_RETRY_COOLDOWN_MS). DM send path only. */
  failFastAfterFailure?: boolean;
  conversationId: string;
  localUserId: string;
  localPub: string;
  localPair?: SeaSigningPair;
  otherUserId: string;
  otherPub: string;
  isInitiator: boolean;
  getLedgerState?: () => LedgerState;
  onRemoteLedgerState?: (otherUserId: string, state: LedgerState) => void | Promise<void>;
  getLedgerDelta?: (remoteState: LedgerState) => Promise<InteractionEvent[]>;
  onRemoteLedgerEvents?: (otherUserId: string, events: InteractionEvent[]) => void | Promise<void>;
  onRemoteMeshFrame?: (otherUserId: string, frame: P2PMeshFrame) => void | Promise<void>;
  /** REQ-P2P-01: persist inbound DMs to local Gun before UI notify. */
  onRemoteDm?: (wire: DmWirePayload['message']) => void | Promise<void>;
  getConversationControlSnapshot?: () => Promise<Record<string, unknown> | null>;
  onRemoteConversationControl?: (
    otherUserId: string,
    metadata: Record<string, unknown>,
  ) => void | Promise<void>;
  /**
   * Phase 5 peer↔peer reconciliation (spec §19.4): the local message-id digest for this
   * conversation, sent on connect so the peer can backfill gaps directly (no hub).
   */
  getLocalMessageDigest?: () => Promise<string[]>;
  /** Phase 5: given the peer's digest ids, return the local wires the peer is missing. */
  getMessagesForBackfill?: (remoteMessageIds: string[]) => Promise<DmWirePayload['message'][]>;
  /**
   * P2P media (no server/gateway): the peer asked for a shared attachment's bytes by cid —
   * return them if this device holds them (the sender reads from its own content node), or
   * null to decline. Streamed back to the peer over the DataChannel in chunks.
   */
  getAttachmentBytesForCid?: (cid: string) => Promise<Uint8Array | null>;
  /** P2P media: the peer streamed an attachment's bytes to us — hand them to the app. */
  onAttachmentBytes?: (cid: string, bytes: Uint8Array) => void;
  /** SDP/ICE frames travel over Gun pub/sub (`gun.get('p2p-signal')`). */
  gun: unknown;
  /**
   * Scenario 2 (§16): synchronous — reads whatever's currently cached
   * (WebAttestationService.getCachedCredential), never fetches. Absent/undefined on any platform
   * without the attestation bridge, or before the first successful verify round trip — both
   * correctly send no buildTrustCredential in the handshake, evaluating to 'unverified-build' on
   * the receiving side.
   */
  getBuildTrustCredential?: () => OfficialBuildCredential | undefined;
  /** This device's own pinned official identity (docs/security/official-build-identifiers.md),
   * so it can independently evaluate a REMOTE peer's presented credential — all three of these
   * are required together for real evaluation; any missing means every remote peer evaluates to
   * 'unverified-build' rather than guessing, which is the safe default absent full config. */
  officialApplicationId?: string;
  officialSigningIdentityHash?: string;
  lookupVerifierKey?: (verifierKeyId: string) => string | undefined;
};

type ContactIdentityResolver = (
  localUserId: string,
  otherUserId: string,
) => string | undefined | Promise<string | undefined>;

let contactIdentityResolver: ContactIdentityResolver | null = null;

/** Supplies the stable identity pinned in the local user's private Contacts record. */
export function setP2PContactIdentityResolver(resolver: ContactIdentityResolver | null): void {
  contactIdentityResolver = resolver;
}

export class P2PConversationSession {
  private _state: P2PConnectionState = 'idle';
  private pc: RTCPeerConnection | null = null;
  private transportGeneration = 0;
  private dc: RTCDataChannel | null = null;
  private signaling: SignalingTransport;
  private stopPolling: (() => void) | null = null;
  private readonly messages: Message[] = [];
  private readonly listeners = new Set<(messages: Message[]) => void>();
  private readonly lastSeenFromOther = new Map<string, string>();
  private connectPromise: Promise<void> | null = null;
  private cancelConnectAttempt: (() => void) | null = null;
  private hasStarted = false;
  private remoteDescriptionSet = false;
  private makingOffer = false;
  /**
   * Correlates answers/ICE with the offer generation they belong to. Each connect
   * cycle (fresh RTCPeerConnection) gets a new offerId; the responder echoes the
   * offerId of the offer it answered. Without this, a STALE answer from a previous
   * timed-out cycle still sitting in the signaling queue gets applied to the new
   * RTCPeerConnection, consuming the one-shot `remoteDescriptionSet` slot so the
   * real answer is ignored — a connect livelock that repeats 10s-timeout cycles
   * under parallel E2E CPU load ("no mesh neighbors" flake).
   */
  private currentOfferId: string | null = null;
  private ledgerLocalSent = false;
  private ledgerRemoteReceived = false;
  private ledgerReady = false;
  /** Fires the `onP2PVersionMismatch` notification at most once per live session instance
   * (the session is cached/reused across reconnects — see sessionRegistry below), not once
   * per handshake exchange, so a flaky connection doesn't repeat the same reminder. */
  private versionMismatchNotified = false;
  /** Phase 5: debounce timer for a gap-triggered re-digest (coalesces bursts). */
  private reDigestTimer: ReturnType<typeof setTimeout> | null = null;
  // P2P-V: bounded nonce cache replaces unbounded Set to cap memory use
  private readonly dataChannelNonces = new BoundedNonceCache();
  /** Reassembly buffers for inbound attachment chunk streams, keyed by cid. */
  private readonly inboundAttachments = new Map<string, { total: number; chunks: Map<number, Uint8Array> }>();
  /** cids we've already requested this session, so a re-render doesn't re-request. */
  private readonly requestedAttachments = new Set<string>();
  // P2P-Q: handshake state
  private localHandshakePayload: P2PHandshakePayload | null = null;
  private handshakeDiagnostics: HandshakeDiagnostics | null = null;
  // Stash remote payload if it arrives before localHandshakePayload is ready
  private pendingRemoteHandshake: P2PHandshakePayload | null = null;
  private secureOffer: GeneratedSecureSessionOffer | null = null;
  private remoteHandshakePayload: P2PHandshakePayload | null = null;
  private secureSessionKey: CryptoKey | null = null;
  private secureSessionId: string | null = null;
  private secureSessionConfirmed = false;
  private secureConfirmSent = false;
  /**
   * Bumped by every resetSecureSession(). Handshake work started before a reset (a slow
   * contact-key lookup, key derivation) checks it after each await and is dropped, so it cannot
   * derive a key for the new channel from the previous channel's remote offer.
   */
  private secureGeneration = 0;
  private secureHandshakeWatchdog: ReturnType<typeof setTimeout> | null = null;
  private readonly pendingEncryptedFrames: EncryptedChannelWirePayload[] = [];
  /** Epoch ms of the most recent transition into 'failed'; null when never failed. */
  private lastFailedAt: number | null = null;
  private readonly stateListeners = new Set<(state: P2PConnectionState) => void>();
  private onLinkUpgradeMessage: ((message: unknown) => void | Promise<void>) | null = null;
  /** OPEN-36 Wi-Fi Direct link: config for the next RTCPeerConnection (setIceConfigOverride). */
  private iceConfigOverride: RTCConfiguration | null = null;
  /** Restart requests already acted on (the signaling queue may deliver one twice). */
  private readonly handledRestarts = new Set<string>();

  constructor(private config: P2PSessionConfig) {
    this.signaling = new CompositeSignalingTransport([
      new GunPubSubSignaler(config.gun, config.localPub, config.otherPub, config.conversationId),
      // A connected link needs signaling only for a renegotiation, which Gun pub/sub pushes; the
      // HTTP fallback slows down meanwhile. Each session polled every 600 ms forever, which on a
      // phone is a request through the embedded node per link per 0.6 s (native-app/39).
      new HttpRelaySignaler(config.apiBase, () => (this._state === 'connected' ? P2P_CONNECTED_SIGNAL_POLL_MS : 600)),
    ]);
  }

  getState(): P2PConnectionState {
    return this._state;
  }

  get otherUserId(): string { return this.config.otherUserId; }
  get otherPub(): string { return this.config.otherPub; }
  get isInitiator(): boolean { return this.config.isInitiator; }

  /** Fires on state transitions only (repeated 'connected' from ICE + DTLS + DataChannel collapse). */
  onStateChange(listener: (state: P2PConnectionState) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  setOnLinkUpgradeMessage(hook: ((message: unknown) => void | Promise<void>) | null): void {
    this.onLinkUpgradeMessage = hook;
  }

  async sendLinkUpgradeMessage(message: unknown): Promise<void> {
    await this.sendChannelFrame({ type: 'link-upgrade', message });
  }

  /** The ICE pair WebRTC is currently using, or null before selection / without a connection. */
  async getSelectedCandidatePair(): Promise<SelectedCandidatePair | null> {
    if (!this.pc) return null;
    try {
      const report = await this.pc.getStats();
      return selectedCandidatePairFromStats(report as unknown as Map<string, Record<string, unknown>>);
    } catch {
      return null;
    }
  }

  /**
   * OPEN-36: RTCConfiguration for every RTCPeerConnection this session creates from now on (null =
   * the default STUN/TURN list). The Wi-Fi Direct upgrade sets a relay-only config that points at
   * this device's loopback TURN relay, whose relay socket sits on the group interface — Android
   * WebView never gathers candidates on that interface itself. Takes effect on `reconnect()`.
   */
  setIceConfigOverride(config: RTCConfiguration | null): void {
    this.iceConfigOverride = config;
  }

  /**
   * Replace the RTCPeerConnection with a fresh one (current ICE config) and wait for it to connect.
   * An ICE restart on the live connection is not enough: Chrome keeps the working old pair selected
   * and never even checks the new lower-priority relay pairs (verified on Android WebView).
   */
  reconnect(timeoutMs = P2P_WEBRTC_CONNECT_TIMEOUT_MS, options: { requestOffer?: boolean } = {}): Promise<void> {
    this.closeTransport();
    this.connectPromise = null;
    this.setState('idle');
    return this.ensureConnected(timeoutMs, { requestOffer: options.requestOffer !== false });
  }

  /**
   * The other side rebuilt its connection (a fresh offer, or a restart request): rebuild ours to
   * match without reporting 'idle' — the Wi-Fi Direct link treats an idle active link as lost and
   * would reconnect on its own, undoing the switch.
   */
  private async rebuildForRemote(): Promise<void> {
    this.closeTransport();
    this.connectPromise = null;
    this._state = 'idle';
    const attempt = this.ensureConnected(P2P_WEBRTC_CONNECT_TIMEOUT_MS, { requestOffer: false });
    void attempt.catch(() => undefined);
    // start() is async up to the new RTCPeerConnection; wait until it exists.
    for (let i = 0; i < 100 && !this.pc && (this._state as P2PConnectionState) === 'connecting'; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
  }

  /** P2P-Q: Returns a snapshot of the handshake negotiation diagnostics. */
  getHandshakeDiagnostics(): HandshakeDiagnostics | null {
    return this.handshakeDiagnostics;
  }

  getMessages(): Message[] {
    return [...this.messages].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }

  getOtherUserId(): string {
    return this.config.otherUserId;
  }

  hasLedgerHooks(): boolean {
    return typeof this.config.getLedgerState === 'function';
  }

  /** Ask an already-connected peer for its current state so we can return its missing delta. */
  synchronizeLedger(): void {
    void this.sendChannelFrame({ type: 'ledger-sync-request' } satisfies LedgerSyncRequestWirePayload)
      .catch(() => undefined);
  }

  subscribe(listener: (messages: Message[]) => void): () => void {
    this.listeners.add(listener);
    listener(this.getMessages());
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const snapshot = this.getMessages();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }

  setLedgerHooks(hooks: {
    getLedgerState?: () => LedgerState;
    onRemoteLedgerState?: (otherUserId: string, state: LedgerState) => void | Promise<void>;
    getLedgerDelta?: (remoteState: LedgerState) => Promise<InteractionEvent[]>;
    onRemoteLedgerEvents?: (otherUserId: string, events: InteractionEvent[]) => void | Promise<void>;
  }): void {
    this.config = { ...this.config, ...hooks };
  }

  setAttachmentHooks(hooks: {
    getAttachmentBytesForCid?: (cid: string) => Promise<Uint8Array | null>;
    onAttachmentBytes?: (cid: string, bytes: Uint8Array) => void;
  }): void {
    this.config = { ...this.config, ...hooks };
  }

  setConversationControlHooks(hooks: {
    getConversationControlSnapshot?: () => Promise<Record<string, unknown> | null>;
    onRemoteConversationControl?: (otherUserId: string, metadata: Record<string, unknown>) => void | Promise<void>;
  }): void {
    this.config = { ...this.config, ...hooks };
  }

  /** Scenario 2 (§16): late-binds the build-trust evaluation context onto an existing (possibly
   * already-cached/reused) session, same pattern as setLedgerHooks/setAttachmentHooks above —
   * sessions are cached by getOrCreateP2PSession, so a hook set on the owning transport AFTER a
   * session already exists still needs to reach it. */
  setBuildTrustHooks(hooks: {
    getBuildTrustCredential?: () => OfficialBuildCredential | undefined;
    officialApplicationId?: string;
    officialSigningIdentityHash?: string;
    lookupVerifierKey?: (verifierKeyId: string) => string | undefined;
  }): void {
    this.config = { ...this.config, ...hooks };
  }

  /** Ask the peer to stream a shared attachment's bytes over the DataChannel (once per cid). */
  async requestAttachment(cid: string): Promise<void> {
    const key = String(cid || '').trim();
    if (!key || this.requestedAttachments.has(key)) return;
    this.requestedAttachments.add(key);
    try {
      await this.sendChannelFrame({ type: 'attach-request', cid: key });
    } catch (e) {
      // Channel not open yet; allow a later retry.
      this.requestedAttachments.delete(key);
    }
  }

  /** Serve a requested attachment: read local bytes and stream them back in signed chunks. */
  private async handleAttachRequest(cid: string): Promise<void> {
    const key = String(cid || '').trim();
    if (!key || !this.config.getAttachmentBytesForCid) return;
    let bytes: Uint8Array | null = null;
    try {
      bytes = await this.config.getAttachmentBytesForCid(key);
    } catch {
      bytes = null;
    }
    if (!bytes || bytes.length === 0) return;
    const CHUNK = 12 * 1024; // keep each signed DataChannel frame comfortably small
    const total = Math.ceil(bytes.length / CHUNK);
    for (let seq = 0; seq < total; seq += 1) {
      const slice = bytes.subarray(seq * CHUNK, (seq + 1) * CHUNK);
      const dataB64 = uint8ToBase64(slice);
      try {
        await this.sendChannelFrame({ type: 'attach-chunk', cid: key, seq, total, dataB64 });
      } catch {
        return; // channel closed mid-stream; recipient can re-request later
      }
    }
  }

  /** Accumulate an inbound attachment chunk; deliver the whole file once complete. */
  private handleAttachChunk(frame: AttachChunkWirePayload): void {
    const key = String(frame.cid || '').trim();
    if (!key || !this.config.onAttachmentBytes) return;
    const total = Number(frame.total) || 0;
    if (total <= 0 || !Number.isFinite(frame.seq)) return;
    let entry = this.inboundAttachments.get(key);
    if (!entry) {
      entry = { total, chunks: new Map() };
      this.inboundAttachments.set(key, entry);
    }
    let bytes: Uint8Array;
    try {
      bytes = base64ToUint8(frame.dataB64);
    } catch {
      return;
    }
    entry.chunks.set(Number(frame.seq), bytes);
    if (entry.chunks.size < entry.total) return;
    // All chunks present — concatenate in order.
    const ordered: Uint8Array[] = [];
    let size = 0;
    for (let i = 0; i < entry.total; i += 1) {
      const c = entry.chunks.get(i);
      if (!c) return; // missing a chunk; wait for it
      ordered.push(c);
      size += c.length;
    }
    const full = new Uint8Array(size);
    let offset = 0;
    for (const c of ordered) { full.set(c, offset); offset += c.length; }
    this.inboundAttachments.delete(key);
    this.config.onAttachmentBytes(key, full);
  }

  setOnRemoteDm(hook: (wire: DmWirePayload['message']) => void | Promise<void>): void {
    this.config.onRemoteDm = hook;
  }

  setOnRemoteMeshFrame(hook: (otherUserId: string, frame: P2PMeshFrame) => void | Promise<void>): void {
    this.config.onRemoteMeshFrame = hook;
  }

  /**
   * ICE/DTLS "connected" counts only once the DataChannel is open (OPEN-52). ICE connects first;
   * reporting "connected" then let callers send into a channel that was not open yet. The mesh's
   * first frame failed with "DataChannel not open", marked the neighbor down, and the next
   * reconcile logged a second "connected" 0–100 ms later. The channel's onopen sets the state.
   */
  private markTransportConnected(): void {
    if (this.dc?.readyState === 'open') this.setState('connected');
  }

  private setState(state: P2PConnectionState): void {
    const changed = this._state !== state;
    this._state = state;
    if (state === 'failed') this.lastFailedAt = Date.now();
    else if (state === 'connected') this.lastFailedAt = null;
    if (!changed) return;
    for (const listener of [...this.stateListeners]) {
      try { listener(state); } catch { /* listeners never disturb the transport */ }
    }
  }

  private markLedgerReady(): void {
    if (this.ledgerReady) return;
    this.ledgerReady = true;
  }

  private sendLedgerState(): void {
    if (!this.config.getLedgerState || !this.dc || this.dc.readyState !== 'open') {
      this.markLedgerReady();
      return;
    }
    const feeds = this.config.getLedgerState();
    void this.sendChannelFrame({ type: 'ledger-state', feeds } satisfies LedgerStateWirePayload)
      .catch(() => undefined);
    this.ledgerLocalSent = true;
    if (this.ledgerRemoteReceived || Object.keys(feeds).length === 0) {
      this.markLedgerReady();
    }
  }

  private async handleLedgerState(feeds: LedgerState): Promise<void> {
    this.ledgerRemoteReceived = true;
    try {
      if (this.config.getLedgerDelta) {
        const events = await this.config.getLedgerDelta(feeds);
        // Keep frames comfortably below common DataChannel message limits. The channel is
        // ordered, so the peer persists these batches before any later DM frame is handled.
        for (let offset = 0; offset < events.length; offset += 32) {
          await this.sendChannelFrame({
            type: 'ledger-events',
            events: events.slice(offset, offset + 32),
          } satisfies LedgerEventsWirePayload);
        }
      }
      if (this.config.onRemoteLedgerState) {
        await this.config.onRemoteLedgerState(this.config.otherUserId, feeds);
      }
    } finally {
      // A failed delta must not leave the link waiting on a handshake that cannot finish.
      if (this.ledgerLocalSent || !this.config.getLedgerState) {
        this.markLedgerReady();
      }
    }
  }

  private async waitForLedgerReady(timeoutMs = 3000): Promise<void> {
    if (this.ledgerReady || !this.config.getLedgerState) return;
    const started = Date.now();
    while (!this.ledgerReady && Date.now() - started < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    this.markLedgerReady();
  }

  private async postSignal(kind: PostSignalingBody['kind'], payload: SignalPayload): Promise<void> {
    if (!this.config.localPair) throw new Error('P2P signaling requires a SEA signing pair');
    const signalCiphertext = encodeSignalingPayload(payload);
    const proof = await createSignedP2PEnvelopeProof({
      pair: this.config.localPair,
      payload: p2pSignalingSigningPayload({
        conversationId: this.config.conversationId,
        kind,
        senderPub: this.config.localPub,
        recipientPub: this.config.otherPub,
        signalCiphertext,
      }),
    });
    const body: PostSignalingBody = {
      kind,
      senderPeerId: proof.peerId,
      senderPub: this.config.localPub,
      recipientPub: this.config.otherPub,
      signalCiphertext,
      timestamp: proof.timestamp,
      payloadHash: proof.payloadHash,
      signature: proof.signature,
      nonce: proof.nonce,
    };
    await this.signaling.post(this.config.conversationId, body);
  }

  ensureConnected(timeoutMs = P2P_WEBRTC_CONNECT_TIMEOUT_MS, options: { requestOffer?: boolean } = {}): Promise<void> {
    if (this._state === 'connected') return Promise.resolve();
    // Never hand back a finished attempt for a session that has since failed.
    if (this.connectPromise && this._state !== 'failed') return this.connectPromise;

    // Fail fast while still inside the post-failure cooldown: a peer that was
    // unreachable a moment ago is very unlikely to have become reachable in the
    // last few seconds, and the caller's offline-mailbox fallback already covers
    // this message. Retrying a fresh full-length ICE/DTLS cycle on every send would
    // otherwise turn "send while peer unreachable" into a repeated multi-second hang.
    if (
      this.config.failFastAfterFailure === true &&
      this._state === 'failed' &&
      this.lastFailedAt !== null &&
      Date.now() - this.lastFailedAt < P2P_WEBRTC_RETRY_COOLDOWN_MS
    ) {
      return Promise.reject(new Error('WebRTC connection timeout (cooldown after recent failure)'));
    }

    if (this._state === 'failed') {
      this.resetTransport();
    }

    const requestOffer = !this.config.isInitiator && (options.requestOffer ?? this.hasStarted);
    this.hasStarted = true;
    const attempt = new Promise<void>((resolve, reject) => {
      let settled = false;
      let check: ReturnType<typeof setInterval> | undefined;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (check) clearInterval(check);
        if (this.cancelConnectAttempt === cancel) this.cancelConnectAttempt = null;
        if (error) reject(error);
        else resolve();
      };
      const cancel = () => finish(new Error('WebRTC connection replaced'));
      const timer = setTimeout(() => {
        if (this._state !== 'connected') {
          finish(new Error('WebRTC connection timeout'));
          this.setState('failed');
          // Free the leaked RTCPeerConnection now; otherwise failed attempts pile up against
          // Chromium's per-renderer connection cap (see closeTransport).
          this.closeTransport();
        }
      }, timeoutMs);
      (timer as { unref?: () => void }).unref?.();
      this.cancelConnectAttempt = cancel;

      void this.start()
        .then(() => {
          if (settled) return;
          if (this._state === 'connected') finish();
          else {
            check = setInterval(() => {
              if (this._state === 'connected') {
                finish();
              } else if (this._state === 'failed') {
                finish(new Error('WebRTC connection failed'));
                this.closeTransport();
              }
            }, 200);
          }
        })
        .catch((err) => {
          finish(err);
        });
    });

    this.connectPromise = attempt;
    void attempt.catch(() => {
      if (this.connectPromise === attempt) this.connectPromise = null;
    });
    // Cached Nearby sessions reopen through ensureConnected after idle disposal, not just
    // reconnect(). The answerer needs a fresh offer in both cases: old signals are deduplicated
    // and the initiator may still consider its old connection live. A coordinated switch or
    // rebuild for an offer already received explicitly suppresses this request.
    if (requestOffer) {
      const request: SignalPayload = { type: 'restart', requestId: `restart_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`, at: Date.now() };
      void this.postSignal('connection-state', request).catch(() => undefined);
    }
    return attempt;
  }

  /**
   * Close and release the RTCPeerConnection/DataChannel without changing state. A failed or
   * timed-out attempt MUST call this: a leaked RTCPeerConnection is never garbage-collected
   * while open, and Chromium caps concurrent connections (~500/renderer). In a many-peer mesh
   * (find-similar: 10 browsers × 9 peers, with retries) failed sessions that don't close their
   * pc accumulate until "Cannot create so many PeerConnections", which then freezes the page.
   */
  private closeTransport(): void {
    this.transportGeneration += 1;
    // Nearby releases idle links and can immediately reopen the cached session. Retire both
    // timers with the old attempt: otherwise its timeout closes the replacement connection.
    this.cancelConnectAttempt?.();
    this.stopPolling?.();
    this.stopPolling = null;
    // Detach first: a discarded channel's onclose fires asynchronously and would otherwise mark
    // the *next* connection attempt failed (seen on the Wi-Fi Direct relay switch, reconnect()).
    if (this.dc) { this.dc.onopen = null; this.dc.onmessage = null; this.dc.onclose = null; this.dc.onerror = null; }
    if (this.pc) { this.pc.onicecandidate = null; this.pc.onconnectionstatechange = null; this.pc.oniceconnectionstatechange = null; this.pc.ondatachannel = null; }
    try { this.dc?.close(); } catch { /* already closed */ }
    try { this.pc?.close(); } catch { /* already closed */ }
    this.dc = null;
    this.pc = null;
    this.remoteDescriptionSet = false;
    this.makingOffer = false;
    this.currentOfferId = null;
  }

  private resetTransport(): void {
    this.closeTransport();
    this.setState('idle');
  }

  private resetSecureSession(): void {
    this.secureGeneration += 1;
    this.clearSecureHandshakeWatchdog();
    this.localHandshakePayload = null;
    this.pendingRemoteHandshake = null;
    this.remoteHandshakePayload = null;
    this.secureOffer = null;
    this.secureSessionKey = null;
    this.secureSessionId = null;
    this.secureSessionConfirmed = false;
    this.secureConfirmSent = false;
    this.pendingEncryptedFrames.splice(0);
    this.ledgerLocalSent = false;
    this.ledgerRemoteReceived = false;
    this.ledgerReady = false;
    this.dataChannelNonces.clear();
  }

  private async start(): Promise<void> {
    if (this._state === 'connecting' || this._state === 'connected') return;
    const generation = this.transportGeneration;
    this.resetSecureSession();
    this.setState('connecting');

    // Create AND FULLY WIRE the RTCPeerConnection before subscribing to signaling.
    // resolveIceServers() is async (even though it resolves synchronously-fast in E2E), which
    // left a real window where startPolling()'s Gun subscription was already live but `this.pc`
    // (and its handlers, including the answerer's `ondatachannel`) were still unset — a peer
    // whose own offer/answer/ice arrived in that window hit handleRemoteSignal's `if (!this.pc)
    // return` guard and was silently dropped with no error and no retry. Confirmed via e2e: on a
    // reconnect attempt, the initiator's offer (posted fast on retry, since resolveIceServers'
    // result is cached) reached the answerer before the answerer's own `await
    // resolveIceServers()` had assigned `this.pc`, permanently stalling both sides' DM channel.
    const iceConfig = this.iceConfigOverride ?? { iceServers: await resolveIceServers(this.config.apiBase) };
    if (generation !== this.transportGeneration) return;
    const pc = this.pc = new RTCPeerConnection(iceConfig);
    pc.onicecandidate = (event) => {
      if (this.pc !== pc) return;
      if (!event.candidate) return;
      void this.postSignal('ice-candidate', {
        type: 'ice',
        candidate: event.candidate.toJSON(),
        ...(this.currentOfferId ? { offerId: this.currentOfferId } : {}),
      }).catch(() => undefined);
    };
    pc.onconnectionstatechange = () => {
      if (this.pc !== pc) return;
      const cs = pc.connectionState;
      if (cs === 'connected') {
        this.markTransportConnected();
      } else if (cs === 'failed' || cs === 'closed' || cs === 'disconnected') {
        this.setState('failed');
        this.connectPromise = null;
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (this.pc !== pc) return;
      const ice = pc.iceConnectionState;
      if (ice === 'connected' || ice === 'completed') {
        this.markTransportConnected();
      } else if (ice === 'failed') {
        this.setState('failed');
        this.connectPromise = null;
      }
    };
    if (!this.config.isInitiator) {
      pc.ondatachannel = (event) => {
        if (this.pc !== pc) return;
        this.dc = event.channel;
        this.attachDataChannel(this.dc);
      };
    }

    this.stopPolling = this.signaling.startPolling(
      this.config.conversationId,
      this.config.localPub,
      async (_envelope, payload) => {
        if (this.pc !== pc) return;
        await this.handleRemoteSignal(payload as SignalPayload);
      },
    );

    if (this.config.isInitiator) {
      this.dc = pc.createDataChannel('iinpublic-dm', { ordered: true });
      this.attachDataChannel(this.dc);
      this.makingOffer = true;
      const offerId = this.currentOfferId = `offer_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      const offer = await pc.createOffer();
      if (this.pc !== pc) return;
      await pc.setLocalDescription(offer);
      if (this.pc !== pc) return;
      await this.postSignal('offer', { type: 'offer', sdp: offer, offerId });
      if (this.pc === pc) this.makingOffer = false;
    }
  }

  private async sendHandshake(): Promise<void> {
    if (!this.config.localPair) return;
    const generation = this.secureGeneration;
    if (!this.secureOffer) {
      const offer = await generateSecureSessionOffer();
      if (generation !== this.secureGeneration) return;
      this.secureOffer ??= offer;
    }
    const peerId = await derivePeerIdFromPub(this.config.localPub);
    if (generation !== this.secureGeneration) return;
    const localBuildTrustCredential = this.config.getBuildTrustCredential?.();
    const payload = buildHandshakePayload({
      peerId,
      userId: this.config.localUserId,
      publicKey: this.config.localPub,
      secureSession: this.secureOffer.offer,
      appVersion: readNativeHostInfo().version,
      ...(localBuildTrustCredential ? { buildTrustCredential: localBuildTrustCredential } : {}),
    });
    this.localHandshakePayload = payload;
    const frame: HandshakeWirePayload = { type: 'handshake', payload };
    await this.sendSignedHandshake(frame);
    if (generation !== this.secureGeneration) return;
    if (this.pendingRemoteHandshake) {
      const remote = this.pendingRemoteHandshake;
      this.pendingRemoteHandshake = null;
      await this.acceptRemoteHandshake(remote, generation);
    }
  }

  private async handleHandshake(payload: unknown, signedPub: string, signedPeerId: string): Promise<void> {
    const generation = this.secureGeneration;
    const validation = validateHandshakePayload(payload);
    if (!validation.ok) return this.failSecureHandshake(validation.reason);
    const remote = validation.payload;
    const derivedPeerId = await derivePeerIdFromPub(remote.publicKey);
    if (generation !== this.secureGeneration) return;
    if (
      remote.userId !== this.config.otherUserId ||
      remote.publicKey !== this.config.otherPub ||
      remote.publicKey !== signedPub ||
      remote.peerId !== signedPeerId ||
      derivedPeerId !== signedPeerId
    ) {
      return this.failSecureHandshake('handshake identity does not match the signed DataChannel peer');
    }
    let pinnedPub: string | undefined;
    try {
      pinnedPub = await contactIdentityResolver?.(this.config.localUserId, this.config.otherUserId);
    } catch {
      // Fail closed and visibly: an unhandled throw here used to leave the session "connected"
      // with no encrypted key, so every send waited out the handshake timeout forever.
      if (generation !== this.secureGeneration) return;
      return this.failSecureHandshake('contact identity lookup failed');
    }
    // The lookup can be slow on phones; a reconnect meanwhile made this handshake stale.
    if (generation !== this.secureGeneration) return;
    if (!pinnedIdentityAccepts(pinnedPub, remote.publicKey)) {
      return this.failSecureHandshake('contact identity key changed');
    }
    if (!this.localHandshakePayload) {
      this.pendingRemoteHandshake = remote;
      return;
    }
    await this.acceptRemoteHandshake(remote, generation);
  }

  private async acceptRemoteHandshake(remote: P2PHandshakePayload, generation: number): Promise<void> {
    if (generation !== this.secureGeneration) return;
    if (!this.localHandshakePayload || !this.secureOffer) return;
    const result = negotiateProtocol(this.localHandshakePayload, remote);
    this.handshakeDiagnostics = {
      ...buildHandshakeDiagnostics(this.localHandshakePayload, remote, result),
      buildTrust: this.computeBuildTrust(remote),
    };
    this.maybeNotifyVersionMismatch();
    if (!result.ok || result.selectedProtocol !== 'iinpublic-p2p-v2') {
      return this.failSecureHandshake(result.ok ? 'encrypted protocol required' : result.reason);
    }
    this.remoteHandshakePayload = remote;
    if (this.secureSessionKey) return;
    try {
      const derived = await deriveSecureSession({
        conversationId: this.config.conversationId,
        privateKey: this.secureOffer.privateKey,
        local: {
          userId: this.localHandshakePayload.userId,
          publicKey: this.localHandshakePayload.publicKey,
          ...this.localHandshakePayload.secureSession,
        },
        remote: {
          userId: remote.userId,
          publicKey: remote.publicKey,
          ...remote.secureSession,
        },
      });
      if (generation !== this.secureGeneration) return;
      this.secureSessionKey = derived.key;
      this.secureSessionId = derived.sessionId;
      if (!this.secureConfirmSent) {
        this.secureConfirmSent = true;
        await this.sendEncryptedFrame({ type: 'secure-confirm', sessionId: derived.sessionId }, true);
      }
      const pending = this.pendingEncryptedFrames.splice(0);
      for (const wire of pending) await this.handleEncryptedChannelFrame(wire);
    } catch {
      if (generation !== this.secureGeneration) return;
      this.failSecureHandshake('could not derive encrypted session');
    }
  }

  private failSecureHandshake(reason: string): void {
    if (this.localHandshakePayload) {
      this.handshakeDiagnostics = {
        ...buildHandshakeDiagnostics(this.localHandshakePayload, this.remoteHandshakePayload, null),
        handshakeState: 'failed',
        failureReason: reason,
      };
    }
    this.setState('failed');
    // A stale resolved attempt would make ensureConnected() report success on a dead session.
    this.connectPromise = null;
    this.clearSecureHandshakeWatchdog();
    this.closeTransport();
  }

  private clearSecureHandshakeWatchdog(): void {
    if (this.secureHandshakeWatchdog) clearTimeout(this.secureHandshakeWatchdog);
    this.secureHandshakeWatchdog = null;
  }

  private async waitForSecureSession(timeoutMs = P2P_WEBRTC_CONNECT_TIMEOUT_MS): Promise<void> {
    const started = Date.now();
    while (!this.secureSessionConfirmed && Date.now() - started < timeoutMs) {
      if (this._state === 'failed') throw new Error('Encrypted P2P handshake failed');
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
    if (!this.secureSessionConfirmed) throw new Error('Encrypted P2P handshake timeout');
  }

  /** Scenario 2 (§16): evaluates the REMOTE peer's presented buildTrustCredential (if any) into
   * one of the four trust labels — see official-build-credential.ts's evaluateBuildTrust for the
   * decision logic. Always resolves to 'unverified-build' if this session's config doesn't carry
   * the official-identity/verifier-key context (officialApplicationId etc. are all optional on
   * P2PSessionConfig), which is the correct behavior for any call site that hasn't wired scenario
   * 2 in yet, not a bug. */
  private computeBuildTrust(remote: P2PHandshakePayload): BuildTrustLabel {
    if (!this.config.officialApplicationId || !this.config.officialSigningIdentityHash || !this.config.lookupVerifierKey) {
      return 'unverified-build';
    }
    return evaluateBuildTrust({
      credential: remote.buildTrustCredential,
      remotePublicKey: remote.publicKey,
      officialApplicationId: this.config.officialApplicationId,
      officialSigningIdentityHash: this.config.officialSigningIdentityHash,
      lookupVerifierKey: this.config.lookupVerifierKey,
      verifySignature: verifyOfficialBuildCredentialSignature,
    });
  }

  /** REQ (scenario 1, 2026-09-29): tell the local user once per session when a P2P peer's
   * handshake reveals a different app version — upgrade reminder if we're older, a feature-
   * compatibility heads-up if we're newer. Fires at most once per live session (see
   * versionMismatchNotified); listeners (app.ts) decide how/whether to surface it. */
  private maybeNotifyVersionMismatch(): void {
    if (this.versionMismatchNotified) return;
    const diagnostics = this.handshakeDiagnostics;
    if (!diagnostics) return;
    if (diagnostics.versionMismatch !== 'local-older' && diagnostics.versionMismatch !== 'local-newer') return;
    if (!diagnostics.remoteAppVersion) return;
    this.versionMismatchNotified = true;
    const event: P2PVersionMismatchEvent = {
      conversationId: this.config.conversationId,
      otherUserId: this.config.otherUserId,
      localVersion: diagnostics.localAppVersion,
      remoteVersion: diagnostics.remoteAppVersion,
      direction: diagnostics.versionMismatch,
    };
    versionMismatchListeners.forEach((listener) => listener(event));
  }

  private attachDataChannel(channel: RTCDataChannel): void {
    channel.onopen = () => {
      this.setState('connected');
      this.clearSecureHandshakeWatchdog();
      this.secureHandshakeWatchdog = setTimeout(() => {
        this.secureHandshakeWatchdog = null;
        if (this.dc === channel && !this.secureSessionConfirmed) {
          this.failSecureHandshake('encrypted handshake not confirmed');
        }
      }, P2P_SECURE_HANDSHAKE_TIMEOUT_MS);
      void this.sendHandshake()
        .then(() => this.waitForSecureSession())
        .then(() => this.sendLedgerState())
        .then(() => this.sendConversationControlSnapshot())
        .then(() => this.sendSyncDigest())
        .catch(() => undefined);
    };
    channel.onmessage = (event) => {
      try {
        const parsed = JSON.parse(String(event.data)) as ChannelWirePayload;
        if (parsed.type === 'encrypted-frame') void this.handleEncryptedChannelFrame(parsed);
        else void this.handleSignedChannelFrame(parsed);
      } catch {
        // ignore malformed frames
      }
    };
    channel.onclose = () => {
      this.setState('failed');
      this.connectPromise = null;
    };
    channel.onerror = () => {
      this.setState('failed');
      this.connectPromise = null;
    };
  }

  /**
   * Phase 5: on connect, advertise the local message-id digest so the peer can backfill
   * any messages we're missing directly over the DataChannel (no hub relay). Guarded +
   * isolated: a no-op without the hook, and errors never disturb the DM channel.
   */
  private async sendSyncDigest(): Promise<void> {
    if (!this.config.getLocalMessageDigest) return;
    try {
      const messageIds = await this.config.getLocalMessageDigest();
      await this.sendChannelFrame({
        type: 'sync-digest',
        conversationId: this.config.conversationId,
        messageIds: messageIds ?? [],
      });
    } catch (err) {
      console.warn(`P2P sync-digest send failed for ${this.config.conversationId}:`, err);
    }
  }

  private async sendConversationControlSnapshot(): Promise<void> {
    if (!this.config.getConversationControlSnapshot) return;
    try {
      const metadata = await this.config.getConversationControlSnapshot();
      if (metadata) await this.sendChannelFrame({ type: 'conversation-control', metadata });
    } catch (err) {
      console.warn(`P2P conversation-control send failed for ${this.config.conversationId}:`, err);
    }
  }

  /**
   * Phase 5: re-advertise our digest after a gap is detected (an incoming DM references a
   * `prevSeen` we don't hold) so the peer backfills the missing middle. Debounced to
   * coalesce a burst of out-of-order frames into a single digest. Strictly additive +
   * guarded: a no-op without the digest hook, and never throws into the DM path.
   */
  private scheduleReDigest(): void {
    if (!this.config.getLocalMessageDigest) return;
    if (this.reDigestTimer) return;
    this.reDigestTimer = setTimeout(() => {
      this.reDigestTimer = null;
      void this.sendSyncDigest();
    }, 250);
  }

  /** Phase 5: peer sent its digest → backfill the messages it is missing as `dm` frames. */
  private async handleSyncDigest(frame: SyncDigestWirePayload): Promise<void> {
    if (!this.config.getMessagesForBackfill || frame.conversationId !== this.config.conversationId) return;
    try {
      const missing = await this.config.getMessagesForBackfill(frame.messageIds ?? []);
      for (const wire of missing) {
        await this.sendChannelFrame({ type: 'dm', message: { ...wire, transport: wire.transport ?? 'direct-p2p' } });
      }
    } catch (err) {
      console.warn(`P2P sync backfill failed for ${this.config.conversationId}:`, err);
    }
  }

  private async sendSignedHandshake(frame: HandshakeWirePayload): Promise<void> {
    if (!this.config.localPair) throw new Error('P2P DataChannel frames require a SEA signing pair');
    if (!this.dc || this.dc.readyState !== 'open') throw new Error('DataChannel not open');
    const proof = await createSignedP2PEnvelopeProof({
      pair: this.config.localPair,
      payload: p2pDataChannelSigningPayload({
        conversationId: this.config.conversationId,
        frame,
      }),
    });
    const signed: SignedChannelWirePayload = {
      type: 'signed-frame',
      peerId: proof.peerId,
      pub: this.config.localPub,
      timestamp: proof.timestamp,
      nonce: proof.nonce,
      payloadHash: proof.payloadHash,
      signature: proof.signature,
      frame,
    };
    this.dc.send(JSON.stringify(signed));
  }

  private async sendEncryptedFrame(
    frame: ChannelFramePayload | SecureConfirmPayload,
    allowBeforeConfirmation = false,
  ): Promise<void> {
    if (!this.config.localPair) throw new Error('P2P DataChannel frames require a SEA signing pair');
    if (!this.dc || this.dc.readyState !== 'open') throw new Error('DataChannel not open');
    if (!allowBeforeConfirmation) await this.waitForSecureSession();
    if (!this.secureSessionKey || !this.secureSessionId) throw new Error('Encrypted P2P session not derived');
    const encrypted = await encryptSecureSessionJson(this.secureSessionKey, this.secureSessionId, frame);
    const signedBody = {
      type: 'encrypted-payload',
      sessionId: this.secureSessionId,
      iv: encrypted.iv,
      ciphertext: encrypted.ciphertext,
    };
    const proof = await createSignedP2PEnvelopeProof({
      pair: this.config.localPair,
      payload: p2pDataChannelSigningPayload({
        conversationId: this.config.conversationId,
        frame: signedBody,
      }),
    });
    const wire: EncryptedChannelWirePayload = {
      type: 'encrypted-frame',
      peerId: proof.peerId,
      pub: this.config.localPub,
      timestamp: proof.timestamp,
      nonce: proof.nonce,
      payloadHash: proof.payloadHash,
      signature: proof.signature,
      sessionId: this.secureSessionId,
      iv: encrypted.iv,
      ciphertext: encrypted.ciphertext,
    };
    this.dc.send(JSON.stringify(wire));
  }

  private async sendChannelFrame(frame: ChannelFramePayload): Promise<void> {
    if (frame.type === 'handshake') return this.sendSignedHandshake(frame);
    return this.sendEncryptedFrame(frame);
  }

  private async handleSignedChannelFrame(parsed: SignedChannelWirePayload): Promise<void> {
    if (parsed?.type !== 'signed-frame' || !parsed.frame || parsed.pub !== this.config.otherPub) return;
    const verification = await verifySignedP2PEnvelopeProof({
      proof: {
        peerId: parsed.peerId,
        pub: parsed.pub,
        timestamp: parsed.timestamp,
        nonce: parsed.nonce,
        payloadHash: parsed.payloadHash,
        signature: parsed.signature,
      },
      payload: p2pDataChannelSigningPayload({
        conversationId: this.config.conversationId,
        frame: parsed.frame,
      }),
      nonceCache: this.dataChannelNonces,
    });
    if (!verification.ok) return;
    if (parsed.frame.type === 'handshake') {
      await this.handleHandshake(parsed.frame.payload, parsed.pub, parsed.peerId);
    }
    // Plaintext application frames are a protocol downgrade and are always discarded.
  }

  private async handleEncryptedChannelFrame(parsed: EncryptedChannelWirePayload): Promise<void> {
    if (
      parsed?.type === 'encrypted-frame' &&
      parsed.pub === this.config.otherPub &&
      !this.secureSessionKey &&
      this.pendingEncryptedFrames.length < 8
    ) {
      this.pendingEncryptedFrames.push(parsed);
      return;
    }
    if (
      parsed?.type !== 'encrypted-frame' ||
      parsed.pub !== this.config.otherPub ||
      !this.secureSessionKey ||
      !this.secureSessionId ||
      parsed.sessionId !== this.secureSessionId
    ) return;
    const signedBody = {
      type: 'encrypted-payload',
      sessionId: parsed.sessionId,
      iv: parsed.iv,
      ciphertext: parsed.ciphertext,
    };
    const verification = await verifySignedP2PEnvelopeProof({
      proof: {
        peerId: parsed.peerId,
        pub: parsed.pub,
        timestamp: parsed.timestamp,
        nonce: parsed.nonce,
        payloadHash: parsed.payloadHash,
        signature: parsed.signature,
      },
      payload: p2pDataChannelSigningPayload({
        conversationId: this.config.conversationId,
        frame: signedBody,
      }),
      nonceCache: this.dataChannelNonces,
    });
    if (!verification.ok) return;
    let frame: ChannelFramePayload | SecureConfirmPayload;
    try {
      frame = await decryptSecureSessionJson<ChannelFramePayload | SecureConfirmPayload>(
        this.secureSessionKey,
        this.secureSessionId,
        { iv: parsed.iv, ciphertext: parsed.ciphertext },
      );
    } catch {
      return;
    }
    if (frame.type === 'secure-confirm') {
      if (frame.sessionId !== this.secureSessionId) return;
      this.secureSessionConfirmed = true;
      this.clearSecureHandshakeWatchdog();
      return;
    }
    if (!this.secureSessionConfirmed || frame.type === 'handshake') return;
    if (frame.type === 'ledger-state') {
      await this.handleLedgerState(frame.feeds || {});
      return;
    }
    if (frame.type === 'ledger-sync-request') {
      this.sendLedgerState();
      return;
    }
    if (frame.type === 'ledger-events') {
      const events = Array.isArray(frame.events) ? frame.events : [];
      // Do not become a third-party ledger relay: this authenticated channel may carry
      // only events authored by the peer whose identity signed the outer frame.
      if (events.some((event) => event?.pubkey !== this.config.otherPub)) return;
      await this.config.onRemoteLedgerEvents?.(
        this.config.otherUserId,
        events,
      );
      return;
    }
    if (frame.type === 'link-upgrade') {
      await this.onLinkUpgradeMessage?.(frame.message);
      return;
    }
    if (frame.type === 'mesh') {
      await this.config.onRemoteMeshFrame?.(this.config.otherUserId, frame.frame);
      return;
    }
    if (frame.type === 'sync-digest') {
      await this.handleSyncDigest(frame);
      return;
    }
    if (frame.type === 'attach-request') {
      await this.handleAttachRequest(frame.cid);
      return;
    }
    if (frame.type === 'attach-chunk') {
      this.handleAttachChunk(frame);
      return;
    }
    if (frame.type === 'conversation-control') {
      if (!frame.metadata || typeof frame.metadata !== 'object') return;
      await this.config.onRemoteConversationControl?.(
        this.config.otherUserId,
        frame.metadata,
      );
      return;
    }
    if (frame.type !== 'dm' || !('message' in frame) || !frame.message) return;
    await this.receiveDm(frame.message);
  }

  /**
   * A peer's DM. The ledger handshake runs first, so its events are stored before the DM; but a
   * DM is never dropped for it — a phone builds its ledger delta from the device core one record
   * at a time, and a DM that arrived meanwhile was lost for good (native-app/37, 2026-10-27).
   * Waits as long as the sender does (`waitForLedgerReady`), then takes the DM.
   */
  private async receiveDm(message: DmWirePayload['message']): Promise<void> {
    if (!this.ledgerReady && this.config.getLedgerState) await this.waitForLedgerReady();
    await this.ingestWireMessage(message);
  }

  private async ingestWireMessage(
    wire: DmWirePayload['message'],
    options?: { skipRemotePersist?: boolean },
  ): Promise<void> {
    if (
      !options?.skipRemotePersist &&
      wire.senderId !== this.config.localUserId &&
      this.config.onRemoteDm
    ) {
      await this.config.onRemoteDm(wire);
    }

    const msg: Message = {
      id: wire.id,
      senderId: wire.senderId,
      text: wire.text,
      timestamp: new Date(wire.timestamp),
      channel: (wire.channel as Message['channel']) || 'public',
      readBy: [],
      isFromChatbot: !!wire.isFromChatbot,
      ...(wire.prevSeen !== undefined ? { prevSeen: wire.prevSeen } : {}),
    };
    if (this.messages.some((m) => m.id === msg.id)) return;
    // Phase 5 gap detection: an inbound message whose predecessor we've never seen means
    // we're missing the middle of the thread — ask the peer to backfill via a re-digest.
    if (messageIntroducesGap(this.messages.map((m) => m.id), wire, this.config.localUserId)) {
      this.scheduleReDigest();
    }
    this.messages.push(msg);
    if (wire.senderId !== this.config.localUserId) {
      this.lastSeenFromOther.set(
        `${this.config.conversationId}:${this.config.localUserId}`,
        wire.id,
      );
    }
    this.notify();
  }

  private async handleRemoteSignal(payload: SignalPayload): Promise<void> {
    if (!this.pc) return;
    if (payload.type === 'restart') {
      if (!this.config.isInitiator || this.handledRestarts.has(payload.requestId)) return;
      if (!Number.isFinite(payload.at) || Math.abs(Date.now() - payload.at) > RESTART_REQUEST_MAX_AGE_MS) return;
      this.handledRestarts.add(payload.requestId);
      await this.rebuildForRemote();
      return;
    }
    if (payload.type === 'offer') {
      if (this.makingOffer) return;
      // The same offer again (the signaling queue replays, or both subscriptions deliver it while
      // the first copy is still being answered): already handled.
      if (payload.offerId && payload.offerId === this.currentOfferId) return;
      // A new offer while this connection already has one: the other side rebuilt its connection
      // (reconnect, the Wi-Fi Direct switch). Applying it to this connection fails — its DTLS
      // identity is new — so every first reconnect failed until ICE gave up on the old one
      // (OPEN-74). Answer it on a fresh connection instead.
      if (this.currentOfferId || this.remoteDescriptionSet || this.pc.signalingState !== 'stable') {
        await this.rebuildForRemote();
        if (!this.pc) return;
      }
      // Track which offer generation we are answering so the initiator can
      // correlate our answer (and discard answers to retired offers).
      this.currentOfferId = payload.offerId ?? null;
      const pc = this.pc;
      const offerId = this.currentOfferId;
      const isCurrent = () => this.pc === pc && this.currentOfferId === offerId;
      await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      if (!isCurrent()) return;
      this.remoteDescriptionSet = true;
      const answer = await pc.createAnswer();
      if (!isCurrent()) return;
      await pc.setLocalDescription(answer);
      if (!isCurrent()) return;
      await this.postSignal('answer', {
        type: 'answer',
        sdp: answer,
        ...(offerId ? { offerId } : {}),
      });
    } else if (payload.type === 'answer') {
      // Drop answers that correspond to a previous (timed-out, reset) offer cycle;
      // applying them would consume `remoteDescriptionSet` and the real answer
      // for the current offer would then be ignored.
      if (payload.offerId && payload.offerId !== this.currentOfferId) return;
      if (this.config.isInitiator && !this.remoteDescriptionSet) {
        const pc = this.pc;
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        if (this.pc !== pc) return;
        this.remoteDescriptionSet = true;
      }
    } else if (payload.type === 'ice' && payload.candidate) {
      // Same correlation for ICE: candidates from a retired connect cycle target a
      // closed RTCPeerConnection on the other side and only pollute this one.
      if (payload.offerId && this.currentOfferId && payload.offerId !== this.currentOfferId) return;
      const droppedPrefix = debugDroppedRemoteCandidatePrefix();
      if (droppedPrefix && isDroppedHostCandidate(payload.candidate, droppedPrefix)) return;
      try {
        await this.pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
      } catch {
        // ignore duplicate/late candidates
      }
    }
  }

  async sendDm(
    senderId: string,
    text: string,
    channel: Message['channel'] = 'public',
    persistedWire?: Partial<DmWirePayload['message']> & Pick<DmWirePayload['message'], 'id' | 'senderId' | 'text' | 'timestamp' | 'channel'>,
  ): Promise<void> {
    await this.ensureConnected();
    await this.waitForLedgerReady();
    if (!this.dc || this.dc.readyState !== 'open') {
      throw new Error('DataChannel not open');
    }
    const prevSeen =
      this.lastSeenFromOther.get(`${this.config.conversationId}:${senderId}`) ?? undefined;
    const message: DmWirePayload['message'] = persistedWire
      ? { ...persistedWire, transport: persistedWire.transport ?? 'direct-p2p' }
      : {
          id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          senderId,
          text,
          timestamp: new Date().toISOString(),
          channel,
          transport: 'direct-p2p',
          ...(prevSeen !== undefined ? { prevSeen } : {}),
        };
    const wire = { type: 'dm' as const, message };
    await this.sendChannelFrame(wire);
    void this.ingestWireMessage(wire.message, { skipRemotePersist: true });
  }

  async sendMeshFrame(frame: P2PMeshFrame): Promise<void> {
    await this.ensureConnected();
    if (!this.dc || this.dc.readyState !== 'open') {
      throw new Error('DataChannel not open');
    }
    await this.sendChannelFrame({ type: 'mesh', frame });
  }

  async sendConversationControl(metadata: Record<string, unknown>): Promise<void> {
    await this.ensureConnected();
    await this.sendChannelFrame({ type: 'conversation-control', metadata });
  }

  dispose(): void {
    if (this.reDigestTimer) {
      clearTimeout(this.reDigestTimer);
      this.reDigestTimer = null;
    }
    this.clearSecureHandshakeWatchdog();
    this.resetTransport();
    this.connectPromise = null;
  }
}

const sessionRegistry = new Map<string, P2PConversationSession>();

export type P2PVersionMismatchEvent = {
  conversationId: string;
  otherUserId: string;
  localVersion: string;
  remoteVersion: string;
  /** 'local-older': remind this device to upgrade. 'local-newer': remind this device that the
   * peer may not understand newer features yet. */
  direction: 'local-older' | 'local-newer';
};

/**
 * A single app-wide subscription point (rather than threading a callback through every
 * P2PSessionConfig-building call site — there are three) for "a P2P peer's handshake revealed a
 * different app version than ours." app.ts subscribes once at startup and surfaces a toast; kept
 * here rather than in p2p-handshake.ts since it's specifically about a *live connection*
 * noticing this, not the pure handshake-diagnostics computation itself.
 */
const versionMismatchListeners = new Set<(event: P2PVersionMismatchEvent) => void>();

export function onP2PVersionMismatch(
  listener: (event: P2PVersionMismatchEvent) => void,
): () => void {
  versionMismatchListeners.add(listener);
  return () => versionMismatchListeners.delete(listener);
}

function sessionKey(conversationId: string, localUserId: string): string {
  return `${conversationId}:${localUserId}`;
}

const sessionCreatedListeners = new Set<(session: P2PConversationSession) => void>();

/** App-wide hook for every newly created session (mesh neighbors and DMs alike) — used by the
 *  Wi-Fi Direct link service, mirroring `onP2PVersionMismatch`'s single subscription point. */
export function onP2PSessionCreated(listener: (session: P2PConversationSession) => void): () => void {
  sessionCreatedListeners.add(listener);
  return () => sessionCreatedListeners.delete(listener);
}

export function getOrCreateP2PSession(config: P2PSessionConfig): P2PConversationSession {
  const key = sessionKey(config.conversationId, config.localUserId);
  const existing = sessionRegistry.get(key);
  if (existing) return existing;
  const session = new P2PConversationSession(config);
  sessionRegistry.set(key, session);
  for (const listener of [...sessionCreatedListeners]) {
    try { listener(session); } catch { /* never block session creation */ }
  }
  return session;
}

/** Every live session, for services that start after some sessions already exist. */
export function listP2PSessions(): P2PConversationSession[] {
  return [...sessionRegistry.values()];
}

export function getP2PSession(
  conversationId: string,
  localUserId: string,
): P2PConversationSession | undefined {
  return sessionRegistry.get(sessionKey(conversationId, localUserId));
}
